import { useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BookOpen, Building2, Calendar, Loader2, ChevronDown, ChevronUp, Pencil, FileText, DollarSign, MoreHorizontal, User, Upload, Trash2, Download, Sparkles, ScrollText } from 'lucide-react';
import type { GridApi } from 'ag-grid-community';
import { format, parseISO } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import PriceBookItemsAGGrid from '@/components/pricebook/PriceBookItemsAGGrid';
import PriceBookFormDialog, { PriceBookFormData } from '@/components/pricebook/PriceBookFormDialog';
import PriceBookItemImportWizard, { PriceBookItemImportRow } from '@/components/pricebook/PriceBookItemImportWizard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { EntityStatusActions } from '@/components/status/EntityStatusActions';
import VectorizeStatusBadge from '@/components/pricebook/VectorizeStatusBadge';
import ChunkViewerDialog from '@/components/pricebook/ChunkViewerDialog';
import { DomainKnowledgeTab } from '@/components/pricebook/DomainKnowledgeTab';
import VectorizationLogsDialog from '@/components/pricebook/VectorizationLogsDialog';
import { BRIEFING_SYSTEM_PROMPT } from '@/config/briefingPrompt';

import GridExportDialog, { type ExportScope } from '@/components/export/GridExportDialog';
import { generateCsv, downloadCsv, getVisibleColumnsFromGrid } from '@/lib/export';
import ExternalSystemInfo from '@/components/ExternalSystemInfo';
import RecordMetadata from '@/components/RecordMetadata';
import { useAuth } from '@/contexts/AuthContext';
import { priceBookService, priceBookItemService, invoiceItemService, invoiceService, profileService, externalSystemService, attachmentService, documentChunkService, statusService, type Attachment, type ServiceContext } from '@/services';
import type { PriceBookItem } from '@/services';
import AttachmentUploadDialog from '@/components/attachments/AttachmentUploadDialog';

export default function PriceBookDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isHeaderOpen, setIsHeaderOpen] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<PriceBookItem[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const gridApiRef = useRef<GridApi<PriceBookItem> | null>(null);
  const currentSearchRef = useRef('');
  const [itemTotalCount, setItemTotalCount] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
  const [selectedAttachments, setSelectedAttachments] = useState<Set<string>>(new Set());
  const [vectorizingAttachmentId, setVectorizingAttachmentId] = useState<string | null>(null);
  const [chunkViewerAttachment, setChunkViewerAttachment] = useState<{ id: string; name: string } | null>(null);
  const [logsAttachment, setLogsAttachment] = useState<{ id: string; name: string } | null>(null);

  const { data: priceBook, isLoading } = useQuery({
    queryKey: ['price-book', id],
    queryFn: async () => {
      const result = await priceBookService.getWithAuditInfo(id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id
  });

  // Query for invoices linked to this price book (with status from statuses table)
  const { data: invoices = [], isLoading: invoicesLoading, refetch: refetchInvoices } = useQuery({
    queryKey: ['price-book-invoices', id],
    queryFn: async () => {
      const result = await invoiceService.listByPriceBook(id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id
  });

  // Query for item counts per invoice
  const { data: invoiceItemCounts = {} } = useQuery({
    queryKey: ['price-book-invoice-item-counts', invoices.map(i => i.id)],
    queryFn: async () => {
      const invoiceIds = invoices.map(inv => inv.id);
      const result = await invoiceItemService.countByInvoices(invoiceIds);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: invoices.length > 0
  });

  // Get profile for company_id
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Lookup external system ID for linking
  const { data: externalSystemDetails } = useQuery({
    queryKey: ['external-system-by-name', priceBook?.external_system, profile?.company_id],
    queryFn: async () => {
      if (!priceBook?.external_system || !profile?.company_id) return null;
      const result = await externalSystemService.getByName(priceBook.external_system, profile.company_id);
      if (result.error) return null;
      return result.data;
    },
    enabled: !!priceBook?.external_system && !!profile?.company_id,
  });

  // Fetch list of external systems for edit dropdown
  const { data: externalSystems = [] } = useQuery({
    queryKey: ['external-systems', profile?.company_id],
    queryFn: async () => {
      const result = await externalSystemService.list(profile!.company_id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!profile?.company_id,
  });

  // Query for attachments
  const { data: attachments = [], isLoading: attachmentsLoading, refetch: refetchAttachments } = useQuery({
    queryKey: ['price-book-attachments', id],
    queryFn: async () => {
      const result = await attachmentService.listByEntity('price_book', id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id,
  });

  // Query for chunk counts per attachment
  const attachmentIds = attachments.map(a => a.id);
  const { data: chunkCounts = {} } = useQuery({
    queryKey: ['attachment-chunk-counts', attachmentIds],
    queryFn: async () => {
      const result = await documentChunkService.getChunkCountsByAttachments(attachmentIds);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: attachmentIds.length > 0,
  });

  // Query for current status name (shares cache with EntityStatusActions)
  const { data: currentStatus } = useQuery({
    queryKey: ['status', priceBook?.status_id],
    queryFn: async () => {
      const result = await statusService.get(priceBook!.status_id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!priceBook?.status_id,
  });

  // Handler for saving external system changes
  const handleSaveExternalSystem = async (system: string | null, recordId: string | null) => {
    if (!priceBook || !profile?.company_id || !user) return;

    const snapshot = {
      external_system: priceBook.external_system,
      external_system_record_id: priceBook.external_system_record_id,
    };

    const result = await priceBookService.update(
      id!,
      { external_system: system, external_system_record_id: recordId },
      snapshot,
      { companyId: profile.company_id, userId: user.id }
    );

    if (result.error) {
      toast.error('Failed to update external system link');
      throw result.error;
    }

    queryClient.invalidateQueries({ queryKey: ['price-book', id] });
    queryClient.invalidateQueries({ queryKey: ['external-system-by-name', system, profile.company_id] });
    toast.success('External system link updated');
  };

  const handleSavePriceBook = async (data: PriceBookFormData) => {
    if (!id || !profile?.company_id || !user?.id) return;
    
    const updatedFields = {
      name: data.name,
      vendor_id: data.vendor_id,
      region_id: data.region_id,
      segment_id: data.segment_id,
      effective_date: data.effective_date,
      expiry_date: data.expiry_date,
    };
    
    const result = await priceBookService.update(
      id,
      updatedFields,
      {
        name: priceBook?.name,
        vendor_id: priceBook?.vendor_id,
        region_id: priceBook?.region_id,
        segment_id: priceBook?.segment_id,
        effective_date: priceBook?.effective_date,
        expiry_date: priceBook?.expiry_date,
      },
      { companyId: profile.company_id, userId: user.id }
    );

    if (result.error) {
      toast.error('Failed to update price book');
      throw result.error;
    }

    toast.success('Price book updated successfully');
    queryClient.invalidateQueries({ queryKey: ['price-book', id] });
  };

  const handleStatusChange = async (toStatusId: string, toStatusName: string, comments?: string) => {
    if (!id || !profile?.company_id || !user?.id) return;
    
    const previousStatusId = priceBook?.status_id;
    const result = await priceBookService.updateStatus(
      id,
      toStatusId,
      previousStatusId || null,
      { companyId: profile.company_id, userId: user.id },
      { comments }
    );

    if (result.error) {
      toast.error('Failed to update status');
      return;
    }

    toast.success(`Status changed to ${toStatusName}`);
    queryClient.invalidateQueries({ queryKey: ['price-book', id] });
  };

  const handleDeletePriceBook = async (priceBookId: string) => {
    if (!profile?.company_id || !user?.id || !priceBook) return;
    
    const context: ServiceContext = { companyId: profile.company_id, userId: user.id };

    // First delete all price book items using service
    const itemsResult = await priceBookItemService.deleteByPriceBook(priceBookId, context);
    if (itemsResult.error) {
      toast.error('Failed to delete price book items');
      throw itemsResult.error;
    }

    // Then delete the price book using service (for history tracking)
    const result = await priceBookService.delete(
      priceBookId,
      priceBook as unknown as Record<string, unknown>,
      context
    );

    if (result.error) {
      toast.error('Failed to delete price book');
      throw result.error;
    }

    toast.success('Price book and all items deleted successfully');
    navigate('/price-books');
  };

  const handleDeleteItems = async () => {
    if (!id || !profile?.company_id || !user?.id) return;
    setIsDeleting(true);
    try {
      const context: ServiceContext = { companyId: profile.company_id, userId: user.id };
      const itemIds = selectedItems.map(item => item.id);
      const result = await priceBookItemService.deleteBatch(itemIds, id, context);
      if (result.error) throw result.error;
      toast.success(`Deleted ${itemIds.length} item${itemIds.length !== 1 ? 's' : ''}`);
      setSelectedItems([]);
      queryClient.invalidateQueries({ queryKey: ['price-book-items-ag', id] });
    } catch (error) {
      toast.error('Failed to delete items');
      console.error(error);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleImportItems = async (items: PriceBookItemImportRow[]) => {
    if (!id || !profile?.company_id || !user?.id) return;

    const context: ServiceContext = { companyId: profile.company_id, userId: user.id };

    // Get max existing sequence
    const existingResult = await priceBookItemService.listByPriceBook(id);
    let maxSequence = 0;
    if (!existingResult.error && existingResult.data.length > 0) {
      maxSequence = existingResult.data[existingResult.data.length - 1].sequence ?? 0;
    }

    const itemsToInsert = items.map((item) => ({
      price_book_id: id,
      sequence: ++maxSequence,
      itemcode: item.itemcode,
      description: item.description,
      price: item.price ?? 0,
      unit: item.unit ?? null,
      discount: item.discount ?? 0,
      supplier_itemcode: item.supplier_itemcode ?? null,
      supplier_description: item.supplier_description ?? null,
      is_active: item.is_active ?? true,
      locked_when_used: item.locked_when_used ?? true,
    }));

    const result = await priceBookItemService.createBatch(itemsToInsert, context);
    if (result.error) throw result.error;

    toast.success(`Imported ${items.length} items`);
    queryClient.invalidateQueries({ queryKey: ['price-book-items-ag', id] });
  };

  // Grid export callbacks
  const handleGridApiReady = useCallback((api: GridApi<PriceBookItem>) => {
    gridApiRef.current = api;
  }, []);

  const handleSearchChange = useCallback((search: string) => {
    currentSearchRef.current = search;
  }, []);

  const handleTotalCountChange = useCallback((count: number) => {
    setItemTotalCount(count);
  }, []);

  // Map for computed columns that don't have a field on the row data
  const computedColumnMap: Record<string, string> = {
    "Disc'd Price": '_discountedPrice',
  };

  const calculateDiscountedPrice = (price: number, discount: number) =>
    price * (1 - discount / 100);

  const handleExport = async (scope: ExportScope) => {
    if (!id) return;
    const gridApi = gridApiRef.current;

    try {
      setIsExporting(true);

      if (scope === 'selected' && gridApi) {
        // Use AG Grid's built-in CSV export for selected rows (already in memory)
        gridApi.exportDataAsCsv({
          onlySelected: true,
          fileName: `price-book-items-selected_${new Date().toISOString().split('T')[0]}.csv`,
        });
        setExportDialogOpen(false);
        return;
      }

      // For 'all' and 'filtered', fetch from the service and generate CSV manually
      let rows: PriceBookItem[];
      if (scope === 'filtered' && currentSearchRef.current.trim()) {
        const result = await priceBookItemService.listByPriceBookFiltered(id, currentSearchRef.current);
        if (result.error) throw result.error;
        rows = result.data;
      } else {
        const result = await priceBookItemService.listByPriceBook(id);
        if (result.error) throw result.error;
        rows = result.data;
      }

      // Determine visible columns from the grid
      const columns = gridApi
        ? getVisibleColumnsFromGrid(gridApi, computedColumnMap)
        : [
            { field: 'sequence', headerName: 'Seq' },
            { field: 'itemcode', headerName: 'Item Code' },
            { field: 'description', headerName: 'Description' },
            { field: 'price', headerName: 'Price' },
            { field: 'unit', headerName: 'Unit of Measure' },
            { field: 'discount', headerName: 'Disc%' },
            { field: '_discountedPrice', headerName: "Disc'd Price" },
            { field: 'is_active', headerName: 'Active' },
          ];

      // Enrich rows with computed fields
      const enrichedRows = rows.map((row) => ({
        ...row,
        _discountedPrice: calculateDiscountedPrice(row.price, row.discount),
      }));

      const csv = generateCsv(enrichedRows as Record<string, unknown>[], columns);
      const suffix = scope === 'filtered' ? 'filtered' : 'all';
      downloadCsv(`price-book-items-${suffix}_${new Date().toISOString().split('T')[0]}.csv`, csv);
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export items');
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return format(parseISO(dateStr), 'MMM d, yyyy');
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const getInvoiceStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: 'badge-warning',
      pending: 'badge-primary',
      approved: 'badge-success',
      paid: 'badge-success',
      rejected: 'badge-destructive',
      cancelled: 'badge-destructive',
    };
    return styles[status] || 'badge-primary';
  };

  const formatStatus = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    const { url, error } = await attachmentService.getSignedUrl(attachment.file_path);
    if (error || !url) {
      toast.error('Failed to generate download link');
      return;
    }
    window.open(url, '_blank');
  };

  const handleDeleteAttachment = async (attachment: Attachment) => {
    if (!profile?.company_id || !user?.id) return;
    setDeletingAttachmentId(attachment.id);
    try {
      const context: ServiceContext = { companyId: profile.company_id, userId: user.id };
      const result = await attachmentService.delete(
        attachment.id,
        attachment.file_path,
        attachment as unknown as Record<string, unknown>,
        context
      );
      if (result.error) {
        toast.error('Failed to delete attachment');
        return;
      }
      toast.success('Attachment deleted');
      refetchAttachments();
    } catch {
      toast.error('Failed to delete attachment');
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const handleToggleAttachmentSelection = (attachmentId: string) => {
    setSelectedAttachments(prev => {
      const next = new Set(prev);
      if (next.has(attachmentId)) {
        next.delete(attachmentId);
      } else {
        next.add(attachmentId);
      }
      return next;
    });
  };

  const handleToggleAllAttachments = (checked: boolean) => {
    if (checked) {
      setSelectedAttachments(new Set(attachments.map(a => a.id)));
    } else {
      setSelectedAttachments(new Set());
    }
  };

  const handleVectorizeAttachment = async () => {
    if (!profile?.company_id || !user?.id || !id) return;
    const selectedId = Array.from(selectedAttachments)[0];
    if (!selectedId) return;

    const attachment = attachments.find(a => a.id === selectedId);
    if (!attachment || !attachment.mime_type?.includes('pdf')) {
      toast.error('Only PDF files can be vectorized');
      return;
    }

    setVectorizingAttachmentId(selectedId);
    try {
      const context: ServiceContext = { companyId: profile.company_id, userId: user.id };
      const result = await documentChunkService.vectorizeDocument(
        selectedId,
        'price_book',
        id,
        context
      );

      if (result.error) {
        toast.error(`Vectorization failed: ${result.error.message}`);
        return;
      }

      toast.success(`Document vectorized: ${result.data?.chunks_created} chunks created`);
      queryClient.invalidateQueries({ queryKey: ['attachment-chunk-counts'] });
      queryClient.invalidateQueries({ queryKey: ['vectorization-logs'] });
    } catch {
      toast.error('Failed to vectorize document');
    } finally {
      setVectorizingAttachmentId(null);
    }
  };

  const handleDeleteChunks = async (attachmentId: string) => {
    if (!profile?.company_id || !user?.id) return;
    const context: ServiceContext = { companyId: profile.company_id, userId: user.id };
    const result = await documentChunkService.deleteByAttachment(attachmentId, context);
    if (result.error) {
      toast.error('Failed to delete chunks');
      return;
    }
    toast.success('Chunks deleted');
    queryClient.invalidateQueries({ queryKey: ['attachment-chunk-counts'] });
    setChunkViewerAttachment(null);
  };

  const selectedAttachmentIsPdf = (() => {
    if (selectedAttachments.size !== 1) return false;
    const selectedId = Array.from(selectedAttachments)[0];
    const attachment = attachments.find(a => a.id === selectedId);
    return attachment?.mime_type?.includes('pdf') ?? false;
  })();

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!profile?.company_id || !user?.id) return;
    
    const context: ServiceContext = { companyId: profile.company_id, userId: user.id };
    
    // Find the invoice to get snapshot for history
    const invoice = invoices.find(i => i.id === invoiceId);
    
    // First delete invoice items using service
    const itemsResult = await invoiceItemService.deleteByInvoice(invoiceId, context);
    if (itemsResult.error) {
      toast.error('Failed to delete invoice items');
      return;
    }

    // Then delete the invoice using service
    const invoiceResult = await invoiceService.delete(
      invoiceId,
      invoice as unknown as Record<string, unknown>,
      context
    );

    if (invoiceResult.error) {
      toast.error('Failed to delete invoice');
      return;
    }

    toast.success('Invoice deleted successfully');
    refetchInvoices();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!priceBook) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Price book not found.</p>
        <button onClick={() => navigate('/price-books')} className="btn-primary mt-4">
          Back to Price Books
        </button>
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      {/* Back Button */}
      <button
        onClick={() => navigate('/price-books')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Price Books
      </button>

      {/* Collapsible Header Section */}
      <Collapsible open={isHeaderOpen} onOpenChange={setIsHeaderOpen}>
        <div className="bg-card border border-border rounded-lg mb-6">
          <CollapsibleTrigger asChild>
            <button className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors rounded-lg">
              {isHeaderOpen ? (
                <div className="flex items-start gap-4 text-left">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                    <BookOpen className="w-7 h-7 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h1 className="text-2xl font-bold text-foreground">{priceBook.name}</h1>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditDialogOpen(true);
                        }}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Building2 className="w-4 h-4" />
                      <span>{priceBook.vendors?.name || 'No vendor'}</span>
                      {(priceBook.list_regions || priceBook.list_segments) && (
                        <>
                          <span className="mx-1">•</span>
                          {priceBook.list_regions && <span>{priceBook.list_regions.display_value}</span>}
                          {priceBook.list_regions && priceBook.list_segments && <span className="mx-1">•</span>}
                          {priceBook.list_segments && <span>{priceBook.list_segments.display_value}</span>}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <BookOpen className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-lg font-semibold text-foreground truncate">
                    {priceBook.name} <span className="text-muted-foreground font-normal">|</span> {priceBook.vendors?.name || 'No vendor'}
                    {priceBook.list_regions && (
                      <span className="text-muted-foreground font-normal"> | {priceBook.list_regions.display_value}</span>
                    )}
                    {priceBook.list_segments && (
                      <span className="text-muted-foreground font-normal"> | {priceBook.list_segments.display_value}</span>
                    )}
                  </span>
                  {currentStatus && (
                    <span className="ml-auto shrink-0 text-sm text-muted-foreground">
                      Status: <span className="font-medium text-foreground">{currentStatus.name}</span>
                    </span>
                  )}
                </div>
              )}
              {isHeaderOpen ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground shrink-0" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />
              )}
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <div className="border-t border-border mx-6" />
            <div className="flex flex-col lg:flex-row gap-6 p-6 pt-6">
              {/* Detail fields - 70% */}
              <div className="lg:w-[70%]">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground">Effective Date</label>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{formatDate(priceBook.effective_date)}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Expiry Date</label>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{formatDate(priceBook.expiry_date)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Actions - 30% */}
              <div className="lg:w-[30%]">
                <EntityStatusActions
                  entityType="price_book"
                  currentStatusId={priceBook.status_id}
                  onStatusChange={handleStatusChange}
                />
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      {/* Tabs */}
      <Tabs defaultValue="items" className="w-full">
        <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-6">
          <TabsTrigger 
            value="items" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
          >
            Items
          </TabsTrigger>
          <TabsTrigger 
            value="attachments"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
          >
            Attachments
          </TabsTrigger>
          <TabsTrigger
            value="domain-knowledge"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
          >
            Domain Knowledge
          </TabsTrigger>
          <TabsTrigger
            value="invoices"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
          >
            Invoices
          </TabsTrigger>
          <TabsTrigger
            value="audit-trail"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
          >
            Audit Trail
          </TabsTrigger>
        </TabsList>

        <TabsContent value="items">
          <PriceBookItemsAGGrid
            priceBookId={priceBook.id}
            onSelectionChanged={setSelectedItems}
            onGridApiReady={handleGridApiReady}
            onSearchChange={handleSearchChange}
            onTotalCountChange={handleTotalCountChange}
            toolbarActions={
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    Actions
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setExportDialogOpen(true)}>
                    <Download className="w-4 h-4 mr-2" />
                    Export Price Book Items
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setImportWizardOpen(true)}>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Price Book Items
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    disabled={selectedItems.length === 0}
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Selected ({selectedItems.length})
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            }
          />
        </TabsContent>

        <TabsContent value="attachments">
          <div className="flex items-center justify-end mb-4 gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={selectedAttachments.size === 0}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Actions
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={!selectedAttachmentIsPdf || vectorizingAttachmentId !== null}
                  onClick={handleVectorizeAttachment}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {vectorizingAttachmentId ? 'Vectorizing...' : 'Vectorize Document'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => setUploadDialogOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload Attachment
            </Button>
          </div>
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            {attachmentsLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : attachments.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No attachments have been uploaded yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={selectedAttachments.size === attachments.length && attachments.length > 0}
                        onCheckedChange={(checked) => handleToggleAllAttachments(!!checked)}
                      />
                    </TableHead>
                    <TableHead>File Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-center">Vectorized</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attachments.map((attachment) => (
                    <TableRow key={attachment.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedAttachments.has(attachment.id)}
                          onCheckedChange={() => handleToggleAttachmentSelection(attachment.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <button
                          className="flex items-center gap-2 text-primary hover:underline"
                          onClick={() => handleDownloadAttachment(attachment)}
                        >
                          <FileText className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{attachment.file_name}</span>
                        </button>
                        {attachment.notes && (
                          <p className="text-xs text-muted-foreground mt-1">{attachment.notes}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {attachment.mime_type
                          ? attachment.mime_type.split('/').pop()?.toUpperCase() || attachment.mime_type
                          : '—'}
                      </TableCell>
                      <TableCell className="text-center">
                        <VectorizeStatusBadge
                          chunkCount={chunkCounts[attachment.id] ?? null}
                          isVectorizing={vectorizingAttachmentId === attachment.id}
                          onClick={() => setChunkViewerAttachment({ id: attachment.id, name: attachment.file_name })}
                        />
                      </TableCell>
                      <TableCell>{formatDateTime(attachment.created)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3 text-muted-foreground" />
                          {attachment.created_by_profile?.full_name || '—'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleDownloadAttachment(attachment)}>
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setLogsAttachment({ id: attachment.id, name: attachment.file_name })}>
                              <ScrollText className="w-4 h-4 mr-2" />
                              View Logs
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              disabled={deletingAttachmentId === attachment.id}
                              onClick={() => handleDeleteAttachment(attachment)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {deletingAttachmentId === attachment.id ? 'Deleting...' : 'Delete'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="domain-knowledge">
          {profile?.company_id && user?.id && (
            <DomainKnowledgeTab priceBookId={priceBook.id} companyId={profile.company_id} userId={user.id} systemPrompt={BRIEFING_SYSTEM_PROMPT} />
          )}
        </TabsContent>

        <TabsContent value="invoices">
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            {invoicesLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No invoices have been linked to this price book yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow
                      key={invoice.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">
                            {invoice.invoice_number || invoice.id.slice(0, 8)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-muted-foreground" />
                          {invoice.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                        </div>
                      </TableCell>
                      <TableCell>{invoiceItemCounts[invoice.id] || 0}</TableCell>
                      <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                      <TableCell>
                        <span className={`badge ${getInvoiceStatusBadge((invoice as any).current_status?.name || 'unknown')}`}>
                          {(invoice as any).current_status?.name || 'Unknown'}
                        </span>
                      </TableCell>
                      <TableCell>{formatDateTime(invoice.created)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/invoices/${invoice.id}`);
                            }}>
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteInvoice(invoice.id);
                              }}
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="audit-trail">
          <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
            Audit trail will be displayed here.
          </div>
        </TabsContent>
      </Tabs>

      {/* Metadata and External System Info Panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <RecordMetadata record={priceBook} />
        <ExternalSystemInfo
          externalSystem={priceBook.external_system}
          externalSystemRecordId={priceBook.external_system_record_id}
          externalSystemId={externalSystemDetails?.id}
          availableSystems={externalSystems.map(s => ({ id: s.id, name: s.name }))}
          onSave={handleSaveExternalSystem}
        />
      </div>

      <AttachmentUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        entityType="price_book"
        entityId={id!}
        attachmentRole="terms"
        onSuccess={() => refetchAttachments()}
      />

      <PriceBookFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        priceBook={priceBook}
        onSave={handleSavePriceBook}
        onDelete={handleDeletePriceBook}
      />

      <PriceBookItemImportWizard
        open={importWizardOpen}
        onOpenChange={setImportWizardOpen}
        onImport={handleImportItems}
      />

      <GridExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        onExport={handleExport}
        selectedCount={selectedItems.length}
        hasActiveFilter={!!currentSearchRef.current.trim()}
        totalCount={itemTotalCount}
        isExporting={isExporting}
        title="Export Price Book Items"
      />

      {chunkViewerAttachment && profile?.company_id && user?.id && (
        <ChunkViewerDialog
          open={!!chunkViewerAttachment}
          onOpenChange={(open) => !open && setChunkViewerAttachment(null)}
          attachmentId={chunkViewerAttachment.id}
          attachmentName={chunkViewerAttachment.name}
          sourceEntityType="price_book"
          sourceEntityId={id!}
          context={{ companyId: profile.company_id, userId: user.id }}
          onRevectorize={() => {
            setChunkViewerAttachment(null);
            setSelectedAttachments(new Set([chunkViewerAttachment.id]));
            handleVectorizeAttachment();
          }}
          onDeleteChunks={() => handleDeleteChunks(chunkViewerAttachment.id)}
          isVectorizing={vectorizingAttachmentId === chunkViewerAttachment.id}
        />
      )}

      {logsAttachment && (
        <VectorizationLogsDialog
          open={!!logsAttachment}
          onOpenChange={(open) => !open && setLogsAttachment(null)}
          attachmentId={logsAttachment.id}
          attachmentName={logsAttachment.name}
        />
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Items</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''}? This action is permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItems}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
